import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { ViewportService } from '@services/geo/viewport.service';
import { SharedModule } from '@shared/modules/shared.module';

export interface TabHeader {
	labelKey?: string;
	label?: string;
	icon?: string;
	disabled?: boolean;
	/** Optional notification badge (count) rendered over the tab icon. Hidden when 0/undefined. */
	badge?: number;
}

@Component({
	selector: 'app-tabs',
	imports: [SharedModule],
	templateUrl: './tabs.component.html',
	styleUrl: './tabs.component.scss',
	host: {
		'[class.tabs-type1]': 'type === "type1"',
		'[class.tabs-type2]': 'type === "type2"',
		'[class.tabs-type3]': 'type === "type3"',
		'[class.tabs-pill]': 'type === "pill"',
		'[class.tabs-large]': 'size === "large"',
		'[class.tabs-medium]': 'size === "medium"',
		'[class.tabs-small]': 'size === "small"',
		'[class.tabs-xsmall]': 'size === "xsmall"',
		'[class.tabs-fit-content]': 'fitContent',
		'[class.tabs-center-selected]': 'centerSelected && isMobile',
	},
})
export class TabsComponent implements AfterViewInit, OnChanges, OnInit {
	@Input() tabHeaders: TabHeader[] = [];
	@Input() type: 'type1' | 'type2' | 'type3' | 'pill' = 'type1';
	@Input() size: 'large' | 'medium' | 'small' | 'xsmall' = 'medium';
	@Input() fitContent = false; // Make tabs fit content width instead of equal distribution
	@Input() iconOnly = false; // Show only icons without labels
	@Input() centerSelected = false; // Center selected tab in viewport (for mobile scroll)

	@Input() selectedTab = 0;
	@Output() selectedTabChange = new EventEmitter<number>();

	isMobile = false;

	constructor(
		private elementRef: ElementRef,
		public viewportService: ViewportService,
	) {}

	ngOnInit(): void {
		this.updateMobileState();
	}

	@HostListener('window:resize')
	onResize(): void {
		this.updateMobileState();
	}

	private updateMobileState(): void {
		this.isMobile = this.viewportService.isMobileView();
	}

	get iconSize(): number {
		return this.size === 'xsmall' ? 16 : 24;
	}

	ngAfterViewInit(): void {
		if (this.centerSelected && this.isMobile) {
			// Small delay to ensure DOM is ready
			setTimeout(() => this.scrollToSelectedTab(false), 150);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedTab'] && !changes['selectedTab'].firstChange && this.centerSelected && this.isMobile) {
			setTimeout(() => this.scrollToSelectedTab(true), 50);
		}
	}

	selectTab(index: number): void {
		if (typeof index === 'number') {
			if (this.tabHeaders[index]?.disabled) return;
			this.selectedTab = index;
			this.selectedTabChange.emit(this.selectedTab);
			if (this.centerSelected && this.isMobile) {
				// Use requestAnimationFrame to ensure DOM has updated
				requestAnimationFrame(() => {
					this.scrollToSelectedTab(true);
				});
			}
		}
	}

	private scrollToSelectedTab(smooth = true): void {
		// Find the p-tablist element which is the scrollable container
		const pTablist = this.elementRef.nativeElement.querySelector('.p-tablist') as HTMLElement;
		if (!pTablist) return;

		// Get the tab list container
		const tabList = pTablist.querySelector('.p-tablist-tab-list') as HTMLElement;
		if (!tabList) return;

		// Get all tab elements
		const tabs = tabList.querySelectorAll('.p-tab');
		if (!tabs || tabs.length === 0) return;

		const selectedTabElement = tabs[this.selectedTab] as HTMLElement;
		if (!selectedTabElement) return;

		// The p-tablist is the scrollable element
		const scrollableElement = pTablist;

		// Get measurements
		const containerWidth = scrollableElement.clientWidth;
		const tabOffsetLeft = selectedTabElement.offsetLeft;
		const tabWidth = selectedTabElement.offsetWidth;

		// Calculate target scroll position to center the selected tab
		const tabCenter = tabOffsetLeft + tabWidth / 2;
		const targetScrollPosition = tabCenter - containerWidth / 2;

		// Clamp to valid scroll bounds
		const maxScroll = scrollableElement.scrollWidth - containerWidth;
		const clampedPosition = Math.max(0, Math.min(targetScrollPosition, maxScroll));

		scrollableElement.scrollTo({
			left: clampedPosition,
			behavior: smooth ? 'smooth' : 'auto',
		});
	}
}
